import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useTranslate } from "../../../../components/TranslationText";
import { TranslationKeys } from "../../../../constants/TranslationKeys";
import {
    ServiceEventNames,
    PaymentTypeMethods,
    PaymentRedirectUrls
} from "../../shared/utils/ServicesConstants";
import { PaymentService } from "../../shared/utils/PaymentService";
import { scrollToFirstError } from "../../shared/utils/formUtils";
import { buildIBCPayload } from "../utils/InternationalBreedingCertificateMappers";
import { CreateOrEditIBCthunk } from "../../../../Redux/InternationalBreedingCertificate/internationalBreedingCertificateAction";

/**
 * Hook responsible for IBC form submission, validation error scrolling, and payment redirection.
 * Completely eliminates mutable refs and setTimeout race conditions by using async unwrap().
 */
export const useIBCSubmission = ({
    methods,
    id,
    serviceId,
    defaultServiceDefId,
    selectedOwnerId,
    requestData,
    hasFees,
    currentLanguage,
    serviceDefinitionData
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { translate } = useTranslate();

    const handleCreateEdit = useCallback(
        (status, isOnlinePayment = false) => {
            methods.handleSubmit(
                async (data) => {
                    try {
                        const payload = buildIBCPayload({
                            data,
                            status,
                            id,
                            serviceId,
                            defaultServiceDefId,
                            selectedOwnerId,
                            requestData
                        });

                        // Dispatch and directly unwrap the thunk promise
                        const response = await dispatch(CreateOrEditIBCthunk(payload)).unwrap();

                        // Check for backend business error codes
                        if (response?.status?.responseCode === "100") {
                            const errorMsg =
                                response?.status?.detailedResponseCode || "Error occurred";
                            toast.error(translate(errorMsg, errorMsg));
                            return;
                        }

                        toast.success(
                            translate(TranslationKeys.DATA_SAVED, "Data Saved Successfully")
                        );

                        // Allow the user to see the success confirmation before redirecting
                        await new Promise((resolve) => setTimeout(resolve, 1000));

                        const isDraft =
                            status?.toLowerCase() === ServiceEventNames.DRAFT.toLowerCase();
                        const targetEntityId =
                            id || response?.result?.id || response?.id || response?.data?.id;

                        if (!isDraft && hasFees) {
                            PaymentService.redirectToPaymentGateway({
                                entityId: targetEntityId,
                                paymentType:
                                    PaymentTypeMethods.INTERNATIONAL_BREEDING_CERTIFICATE,
                                returnUrl:
                                    PaymentRedirectUrls.getEServicesRedirectUrl(targetEntityId),
                                lang: currentLanguage,
                                title:
                                    currentLanguage === "ar"
                                        ? serviceDefinitionData?.nameAr
                                        : serviceDefinitionData?.title,
                                isOnline: isOnlinePayment
                            });
                        } else {
                            navigate(PaymentRedirectUrls.getEservicesUrl());
                        }
                    } catch (error) {
                        const message =
                            typeof error === "string"
                                ? error
                                : error?.message || "An unexpected error occurred";
                        toast.error(translate(message, message));
                    }
                },
                (errors) => {
                    toast.error(
                        translate(
                            TranslationKeys.PLEASE_FILL_IN_ALL_FIELDS,
                            "Please fill in all required fields"
                        )
                    );
                    scrollToFirstError(errors);
                }
            )();
        },
        [
            dispatch,
            id,
            serviceId,
            defaultServiceDefId,
            selectedOwnerId,
            requestData,
            methods,
            hasFees,
            currentLanguage,
            serviceDefinitionData,
            navigate,
            translate
        ]
    );

    return { handleCreateEdit };
};
